/**
 * Shared TanStack Query client + centralized query keys.
 *
 * Server data lives here (TanStack Query); Zustand is reserved for UI/session
 * state only. Keys are centralized so mutations can invalidate consistently.
 */

import { QueryCache, QueryClient } from "@tanstack/react-query";
import { isApiException, isUnauthorized } from "./api-errors";
import { clearTokens } from "./auth-token";
import { useAuthStore } from "@/features/auth/auth.store";
import { toast } from "@/stores/toast.store";

/**
 * Global handler for an authenticated request that comes back 401 mid-session
 * (the access token expired/was revoked while the app was open). End the session
 * once and tell the user. 403 (insufficient permission) is left alone — it is not
 * an authentication failure. NetworkError never reaches here (it is not an
 * ApiException), so a backend outage does not log the user out.
 */
function handleSessionExpiry(error: unknown): void {
  if (!isApiException(error) || error.status !== 401) return;
  if (useAuthStore.getState().status !== "authenticated") return;
  clearTokens();
  useAuthStore.getState().setAnonymous();
  queryClient.removeQueries({ queryKey: queryKeys.currentUser });
  toast.error("Session expirée, veuillez vous reconnecter");
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => handleSessionExpiry(error),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Never retry auth/permission failures; retry transient errors once.
      retry: (failureCount, error) => (isUnauthorized(error) ? false : failureCount < 1),
    },
  },
});

export const queryKeys = {
  currentUser: ["auth", "me"] as const,
  clientsList: (params: unknown) => ["clients", "list", params] as const,
  client: (id: string) => ["clients", "detail", id] as const,
  dashboard: (period: string) => ["reports", "dashboard", period] as const,
  paymentsList: (params: unknown) => ["payments", "list", params] as const,
  payment: (id: string) => ["payments", "detail", id] as const,
  walletsList: (params: unknown) => ["wallets", "list", params] as const,
  walletAlerts: (params: unknown) => ["wallets", "alerts", params] as const,
  walletTransactions: (params: unknown) => ["wallet-transactions", "list", params] as const,
  adSpendList: (params: unknown) => ["ad-spend", "list", params] as const,
  expensesList: (params: unknown) => ["expenses", "list", params] as const,
  tasksList: (params: unknown) => ["tasks", "list", params] as const,
  myTasks: ["tasks", "my"] as const,
  task: (id: string) => ["tasks", "detail", id] as const,
  subscriptionsList: (params: unknown) => ["subscriptions", "list", params] as const,
  teamList: (params: unknown) => ["team", "list", params] as const,
  teamDetail: (id: string) => ["team", "detail", id] as const,
  teamMe: ["team", "me"] as const,
  settingsList: ["settings", "list"] as const,
  notificationsList: (params: unknown) => ["notifications", "list", params] as const,
};

/** Prefixes used to invalidate a feature's queries (list + detail) after a mutation. */
export const REPORTS_QUERY_PREFIX = ["reports"] as const;
export const CLIENTS_QUERY_PREFIX = ["clients"] as const;
export const PAYMENTS_QUERY_PREFIX = ["payments"] as const;
export const WALLETS_QUERY_PREFIX = ["wallets"] as const;
export const AD_SPEND_QUERY_PREFIX = ["ad-spend"] as const;
export const EXPENSES_QUERY_PREFIX = ["expenses"] as const;
export const TASKS_QUERY_PREFIX = ["tasks"] as const;
export const SUBSCRIPTIONS_QUERY_PREFIX = ["subscriptions"] as const;
export const TEAM_QUERY_PREFIX = ["team"] as const;
export const SETTINGS_QUERY_PREFIX = ["settings"] as const;
export const NOTIFICATIONS_QUERY_PREFIX = ["notifications"] as const;
